import React from 'react';
import { Platform, ScrollView, StyleSheet, View } from 'react-native';
import { Button, IconButton, Menu, Text } from 'react-native-paper';
import type {
  QuestionField,
  QuestionMapping,
  ReviewItem
} from './shareItemMapping';
import {
  ItemLevelShareOptions,
  QuestionCopyTable
} from './shareItemSections';

type WebSelectChangeEvent = {
  target: {
    value: string;
  };
};

interface ExistingItemQuestionMappingSectionProps {
  targetItem: ReviewItem;
  sourceFields: QuestionField[];
  questionMappings: QuestionMapping[];
  questionsToCopy: Set<string>;
  includeRoom: boolean;
  includePhotos: boolean;
  includeFloor: boolean;
  sourceMappingMenuId: string | null;
  targetMappingMenuId: string | null;
  getCompatibleTargets: (sourceField: QuestionField) => QuestionField[];
  onToggleRoom: () => void;
  onTogglePhotos: () => void;
  onToggleFloor: () => void;
  onUpdateMapping: (mappingId: string, field: 'sourceId' | 'targetId', value: string) => void;
  onRemoveMapping: (mappingId: string) => void;
  onAddMapping: () => void;
  onToggleCopy: (sourceQuestionId: string) => void;
  onOpenSourceMappingMenu: (mappingId: string) => void;
  onDismissSourceMappingMenu: () => void;
  onOpenTargetMappingMenu: (mappingId: string) => void;
  onDismissTargetMappingMenu: () => void;
}

interface QuestionMappingRowProps {
  mapping: QuestionMapping;
  sourceFields: QuestionField[];
  questionMappings: QuestionMapping[];
  sourceMappingMenuId: string | null;
  targetMappingMenuId: string | null;
  getCompatibleTargets: (sourceField: QuestionField) => QuestionField[];
  onUpdateMapping: (mappingId: string, field: 'sourceId' | 'targetId', value: string) => void;
  onRemoveMapping: (mappingId: string) => void;
  onOpenSourceMappingMenu: (mappingId: string) => void;
  onDismissSourceMappingMenu: () => void;
  onOpenTargetMappingMenu: (mappingId: string) => void;
  onDismissTargetMappingMenu: () => void;
}

const QuestionMappingRow: React.FC<QuestionMappingRowProps> = ({
  mapping,
  sourceFields,
  questionMappings,
  sourceMappingMenuId,
  targetMappingMenuId,
  getCompatibleTargets,
  onUpdateMapping,
  onRemoveMapping,
  onOpenSourceMappingMenu,
  onDismissSourceMappingMenu,
  onOpenTargetMappingMenu,
  onDismissTargetMappingMenu
}) => {
  const sourceField = sourceFields.find(f => f.id === mapping.sourceId);
  const compatibleTargets = sourceField ? getCompatibleTargets(sourceField) : [];
  const mappedSourceIds = questionMappings.map(m => m.sourceId);
  const availableSources = sourceFields.filter(sf =>
    !mappedSourceIds.includes(sf.id) || sf.id === mapping.sourceId
  );

  if (!sourceField) return null;

  return (
    <View style={styles.mappingRow}>
      <View style={styles.mappingRowContent}>
        <View style={styles.dropdownContainer}>
          <Text style={styles.dropdownLabel}>Source:</Text>
          {Platform.OS === 'web' ? (
            <select
              value={mapping.sourceId}
              onChange={(event: WebSelectChangeEvent) => onUpdateMapping(mapping.id, 'sourceId', event.target.value)}
              style={styles.select as unknown as React.CSSProperties}
            >
              {availableSources.map(sf => (
                <option key={sf.id} value={sf.id}>
                  {sf.label} ({sf.type})
                </option>
              ))}
            </select>
          ) : (
            <Menu
              visible={sourceMappingMenuId === mapping.id}
              onDismiss={onDismissSourceMappingMenu}
              anchor={
                <Button
                  mode="outlined"
                  onPress={() => onOpenSourceMappingMenu(mapping.id)}
                  style={styles.mappingSelector}
                  contentStyle={styles.selectorContent}
                >
                  {sourceField.label} ({sourceField.type})
                </Button>
              }
            >
              <ScrollView style={styles.menuScrollArea}>
                {availableSources.map(sf => (
                  <Menu.Item
                    key={sf.id}
                    testID={`share-source-question-${mapping.id}-${sf.id}`}
                    title={`${sf.label} (${sf.type})`}
                    onPress={() => {
                      onUpdateMapping(mapping.id, 'sourceId', sf.id);
                      onDismissSourceMappingMenu();
                    }}
                  />
                ))}
              </ScrollView>
            </Menu>
          )}
        </View>

        <View style={styles.dropdownContainer}>
          <Text style={styles.dropdownLabel}>Target:</Text>
          {Platform.OS === 'web' ? (
            <select
              value={mapping.targetId}
              onChange={(event: WebSelectChangeEvent) => onUpdateMapping(mapping.id, 'targetId', event.target.value)}
              disabled={compatibleTargets.length === 0}
              style={styles.select as unknown as React.CSSProperties}
            >
              {compatibleTargets.length > 0 ? (
                compatibleTargets.map(tf => (
                  <option key={tf.id} value={tf.id}>
                    {tf.label} ({tf.type})
                  </option>
                ))
              ) : (
                <option value="">No compatible targets</option>
              )}
            </select>
          ) : (
            <Menu
              visible={targetMappingMenuId === mapping.id}
              onDismiss={onDismissTargetMappingMenu}
              anchor={
                <Button
                  mode="outlined"
                  onPress={() => onOpenTargetMappingMenu(mapping.id)}
                  style={styles.mappingSelector}
                  contentStyle={styles.selectorContent}
                  disabled={compatibleTargets.length === 0}
                >
                  {compatibleTargets.length > 0
                    ? `${mapping.targetLabel} (${mapping.targetType})`
                    : 'No compatible targets'}
                </Button>
              }
            >
              <ScrollView style={styles.menuScrollArea}>
                {compatibleTargets.map(tf => (
                  <Menu.Item
                    key={tf.id}
                    testID={`share-target-question-${mapping.id}-${tf.id}`}
                    title={`${tf.label} (${tf.type})`}
                    onPress={() => {
                      onUpdateMapping(mapping.id, 'targetId', tf.id);
                      onDismissTargetMappingMenu();
                    }}
                  />
                ))}
              </ScrollView>
            </Menu>
          )}
        </View>
      </View>

      <IconButton
        icon="close"
        size={20}
        onPress={() => onRemoveMapping(mapping.id)}
        style={styles.removeButton}
      />
    </View>
  );
};

export const ExistingItemQuestionMappingSection: React.FC<ExistingItemQuestionMappingSectionProps> = ({
  targetItem,
  sourceFields,
  questionMappings,
  questionsToCopy,
  includeRoom,
  includePhotos,
  includeFloor,
  sourceMappingMenuId,
  targetMappingMenuId,
  getCompatibleTargets,
  onToggleRoom,
  onTogglePhotos,
  onToggleFloor,
  onUpdateMapping,
  onRemoveMapping,
  onAddMapping,
  onToggleCopy,
  onOpenSourceMappingMenu,
  onDismissSourceMappingMenu,
  onOpenTargetMappingMenu,
  onDismissTargetMappingMenu
}) => {
  const mappableFields = sourceFields.filter((sourceField) => getCompatibleTargets(sourceField).length > 0);
  const copyOnlyFields = sourceFields.filter((sourceField) => getCompatibleTargets(sourceField).length === 0);

  return (
    <View style={styles.section}>
      <Text style={styles.sectionTitle}>4. Map Questions</Text>

      <ItemLevelShareOptions
        includeRoom={includeRoom}
        includePhotos={includePhotos}
        includeFloor={includeFloor}
        onToggleRoom={onToggleRoom}
        onTogglePhotos={onTogglePhotos}
        onToggleFloor={onToggleFloor}
      />

      {mappableFields.length > 0 && (
        <>
          <Text style={styles.subtitle}>
            Map to existing questions in "{targetItem.name}":
          </Text>

          {questionMappings.map((mapping) => (
            <QuestionMappingRow
              key={mapping.id}
              mapping={mapping}
              sourceFields={sourceFields}
              questionMappings={questionMappings}
              sourceMappingMenuId={sourceMappingMenuId}
              targetMappingMenuId={targetMappingMenuId}
              getCompatibleTargets={getCompatibleTargets}
              onUpdateMapping={onUpdateMapping}
              onRemoveMapping={onRemoveMapping}
              onOpenSourceMappingMenu={onOpenSourceMappingMenu}
              onDismissSourceMappingMenu={onDismissSourceMappingMenu}
              onOpenTargetMappingMenu={onOpenTargetMappingMenu}
              onDismissTargetMappingMenu={onDismissTargetMappingMenu}
            />
          ))}

          <Button
            mode="outlined"
            onPress={onAddMapping}
            style={styles.addMappingButton}
            icon="plus"
          >
            Add Mapping
          </Button>
        </>
      )}

      {copyOnlyFields.length > 0 && (
        <>
          <Text style={styles.copySubtitle}>
            Copy new questions to "{targetItem.name}":
          </Text>

          <QuestionCopyTable
            fields={sourceFields}
            questionsToCopy={questionsToCopy}
            onToggleCopy={onToggleCopy}
            filterField={(sourceField) => getCompatibleTargets(sourceField).length === 0}
            selectColumnTitle="Select"
            uncheckedColor="#9E9E9E"
          />
        </>
      )}

      <View style={styles.mappingSummary}>
        <Text style={styles.mappingCount}>
          {questionMappings.length} of {mappableFields.length} mapped, {questionsToCopy.size} of {copyOnlyFields.length} to copy
        </Text>
        {questionsToCopy.size > 0 && (
          <Text style={styles.hint}>
            New questions will be added to "{targetItem.name}" and automatically shared.
          </Text>
        )}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  section: {
    marginBottom: 20
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 8
  },
  subtitle: {
    fontSize: 14,
    color: '#666',
    marginBottom: 16
  },
  copySubtitle: {
    fontSize: 14,
    color: '#666',
    marginBottom: 16,
    marginTop: 16
  },
  mappingRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 12,
    padding: 12,
    backgroundColor: '#F9F9F9',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E0E0E0'
  },
  mappingRowContent: {
    flex: 1,
    gap: 12
  },
  dropdownContainer: {
    marginBottom: 8
  },
  dropdownLabel: {
    fontSize: 12,
    fontWeight: '600',
    color: '#666',
    marginBottom: 4
  },
  select: {
    width: '100%',
    height: 40,
    padding: 8,
    borderWidth: 1,
    borderColor: '#CCC',
    borderRadius: 4,
    backgroundColor: 'white',
    fontSize: 14,
    fontFamily: 'inherit'
  },
  selectorContent: {
    justifyContent: 'flex-start'
  },
  mappingSelector: {
    width: '100%'
  },
  menuScrollArea: {
    maxHeight: 300
  },
  removeButton: {
    margin: 0,
    marginLeft: 8
  },
  addMappingButton: {
    marginTop: 8,
    marginBottom: 16
  },
  mappingSummary: {
    marginTop: 12,
    padding: 12,
    backgroundColor: '#E3F2FD',
    borderRadius: 4
  },
  mappingCount: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1976D2',
    marginBottom: 4
  },
  hint: {
    fontSize: 12,
    color: '#666',
    fontStyle: 'italic'
  }
});
